import { useGetFolders } from '../useGetFolders';
import { BLANK } from '../../../constant/blank';
import { match, P } from 'ts-pattern';
import { toast } from '../toast';
import { bridge, delay, isIOS } from '../../../utils';
import { useOpenDeeplink } from '../../useOpenDeeplink';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { likeGoodsService } from '../../../api';
import {
  Typography,
  ColorValues,
  Flex,
  IconAdd,
  IconHeartOutline,
  useScreenSize,
  List,
  RemoteImage,
  createUseBottomSheetModal,
  BottomSheetContainer,
} from '@banhala/ui-kit';
import { queryKey } from '../../../constant';
import { useEffect, useMemo } from 'react';
import type { LikeFolder } from '../../../api/likedFolderService/getLikedFolders/types';
import Toast from 'react-native-toast-message';

// show 함수의 파라미터
type UseCreateLikeFolderBottomSheetShowProps = {
  goodsSno: number;
  screen: string;
  listParams?: string;
  hideToast?: boolean;
  onChangeIsLiked: (sno: number, nextValue: boolean) => void;
};
type ConfirmReturnType = {
  showCreateNewLikeFolder: boolean;
  folder?: {
    sno: number;
    name: string;
  };
};

// 네이티브 바텀시트 콘텐츠 높이에 맞춤 (타이틀 영역 제외)
// - iOS: LikedFolderPickerVC collectionView offset 530
// - Android: FolderBottomSheet FolderChooseScreen 500dp - 타이틀 56dp (타이틀 포함 높이이므로 차감)
const BOTTOM_SHEET_TITLE_HEIGHT = 56;
const CONTENT_HEIGHT = isIOS ? 530 : 500 - BOTTOM_SHEET_TITLE_HEIGHT;

const useLogViewChoiceFolder = (screen: string) => {
  useEffect(() => {
    bridge.logAnalyticsEvent({
      name: 'VIEW_CHOICE_FOLDER',
      analytics: {
        SCREEN: screen,
      },
    });
  }, [screen]);
};
export const useCreateLikeFolderBottomSheet = createUseBottomSheetModal<
  ConfirmReturnType,
  UseCreateLikeFolderBottomSheetShowProps
>(
  ({
    cancel,
    param: { goodsSno, onChangeIsLiked, screen, listParams, hideToast },
    confirm,
  }) => {
    useLogViewChoiceFolder(screen);

    return (
      <BottomSheetContainer
        title={'찜할 서랍 선택'}
        onPressClose={cancel}
        maxHeight={'70%'}
      >
        <LikeFolderList
          listParams={listParams}
          goodsSno={goodsSno}
          confirm={confirm}
          screen={screen}
          cancel={cancel}
          onChangeIsLiked={onChangeIsLiked}
          hideToast={hideToast}
        />
      </BottomSheetContainer>
    );
  },
);

type LikeFolderListProps = {
  confirm: (data: ConfirmReturnType) => void;
  cancel: VoidFunction;
  screen: string;
} & UseCreateLikeFolderBottomSheetShowProps;
const LikeFolderList = ({
  cancel,
  goodsSno,
  confirm,
  onChangeIsLiked,
  listParams,
  hideToast,
  screen,
}: LikeFolderListProps) => {
  const { folders, getNextFolders, hasNextPage } = useGetFolders();
  const folderList = useMemo(() => {
    return folders ?? [];
  }, [folders]);

  return (
    <List<LikeFolder>
      data={folderList}
      style={{
        height: CONTENT_HEIGHT,
      }}
      renderItem={({ item: { count, webview_goods_images, title, sno } }) => (
        <ListLayout
          cancel={cancel}
          screen={screen}
          count={count}
          title={title}
          webview_goods_images={webview_goods_images}
          sno={sno}
          goodsSno={goodsSno}
          confirm={confirm}
          onChangeIsLiked={onChangeIsLiked}
          listParams={listParams}
          hideToast={hideToast}
        />
      )}
      stickyHeaderIndices={[0]}
      ListHeaderComponent={
        <ListLayout
          screen={screen}
          header={true}
          confirm={confirm}
          hideToast={hideToast}
          onChangeIsLiked={onChangeIsLiked}
        />
      }
      showsVerticalScrollIndicator={false}
      onEndReachedThreshold={0.5}
      onEndReached={() => {
        if (hasNextPage) {
          getNextFolders();
        }
      }}
    />
  );
};

type ListLayoutProps = {
  confirm: (data: ConfirmReturnType) => void;
  cancel?: VoidFunction;
  hideToast?: boolean;
} & (
  | {
      header: true;
      count?: undefined;
      title?: undefined;
      webview_goods_images?: undefined;
      sno?: undefined;
      goodsSno?: undefined;
      onChangeIsLiked: (sno: number, nextValue: boolean) => void;
      listParams?: string;
      screen: string;
    }
  | {
      header?: undefined;
      count: number;
      title: string;
      webview_goods_images: Array<string>;
      sno: number;
      goodsSno: number;
      onChangeIsLiked: (sno: number, nextValue: boolean) => void;
      listParams?: string;
      screen: string;
    }
);

function ListLayout({
  header,
  count,
  title,
  webview_goods_images,
  sno,
  goodsSno,
  confirm,
  onChangeIsLiked,
  listParams,
  screen,
  cancel,
  hideToast,
}: ListLayoutProps) {
  const queryClient = useQueryClient();
  const { openDeeplink } = useOpenDeeplink();
  const { mutate: mutateLikeGoods } = useMutation({
    mutationFn: likeGoodsService.post,
    onSuccess: async (_, { folder_sno, sno }) => {
      if (header) {
        return;
      }
      onChangeIsLiked(sno, true);
      await delay(400);
      queryClient.invalidateQueries({
        queryKey: queryKey.getInfiniteLikeFolders,
      });
      if (!hideToast) {
        toast({
          folderName: title,
          folderSno: folder_sno,
          screen,
          loggingAnalytics: { GOODS_SNO: sno },
          openDeeplink,
        });
      }
      bridge.likeGoods({
        goods_sno: sno,
        is_liked: true,
        folder_sno,
      });
      confirm({
        showCreateNewLikeFolder: false,
        folder: {
          name: title,
          sno: folder_sno,
        },
      });
    },
    onError: error => {
      let errorMessage = error?.message ?? '오류가 발생했어요';
      Toast.show({
        type: 'success',
        text1: errorMessage,
        position: 'bottom',
      });
      cancel?.();
    },
    throwOnError: false,
  });

  const { width } = useScreenSize();

  const folderName = match(header)
    .with(true, () => (
      <Typography type={'subtitle2'} color={'content_primary'}>
        새 서랍 만들기
      </Typography>
    ))
    .otherwise(() => {
      return (
        <>
          <Typography
            type={'subtitle2'}
            color={'content_primary'}
            maxWidth={width - 120}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {title}
          </Typography>
          <Typography
            type={'body2'}
            color={'content_secondary'}
          >{`${BLANK}(${count})`}</Typography>
        </>
      );
    });

  const thumbnail = match({ header, webview_goods_images })
    .with({ header: true }, () => (
      <IconAdd fill={ColorValues.blue40} width={20} height={20} />
    ))
    .with(
      {
        webview_goods_images: P.when(
          (webview_goods_images): webview_goods_images is Array<string> =>
            webview_goods_images !== undefined &&
            webview_goods_images.length > 0,
        ),
      },
      ({ webview_goods_images }) => (
        <RemoteImage width={44} height={44} uri={webview_goods_images[0]} />
      ),
    )
    .otherwise(() => (
      <IconHeartOutline width={20} height={20} fill={ColorValues.gray40} />
    ));

  return (
    <Flex.Row
      padding={{ horizontal: 16, vertical: 12 }}
      alignItems="center"
      backgroundColor="bg_always_white"
      onPress={async () => {
        if (header) {
          bridge.logAnalyticsEvent({
            name: 'CLICK_MAKE_NEW_FOLDER',
            analytics: {
              SCREEN: screen,
            },
          });
          confirm({ showCreateNewLikeFolder: true });
          return;
        }
        mutateLikeGoods({
          folder_sno: sno,
          list_params_referrer: listParams,
          sno: goodsSno,
        });
        confirm({
          showCreateNewLikeFolder: false,
          folder: { sno, name: title },
        });
      }}
    >
      <Flex.Center
        width={44}
        height={44}
        margin={{ right: 16 }}
        position={'relative'}
        overflow={'hidden'}
        borderRadius={'medium'}
        backgroundColor={'bg_secondary'}
      >
        {thumbnail}
      </Flex.Center>
      {folderName}
    </Flex.Row>
  );
}
